import {
  Button,
  Card,
  Container,
  Group,
  Overlay,
  Text,
  Title,
  Grid,
  Space,
  Box,
  Image,
} from '@mantine/core';
import { FC } from 'react';
import DoctorOfficeImage from '../../img/landingPage/doctorOfficeBW.jpg';
import classes from './LandingPage.module.css';
import { useTranslation } from 'react-i18next';
import { useMedplumNavigate } from '@medplum/react';
import { useLanguageSelector } from '@hooks/useLanguageSelector';
import {
  IconCalendar,
  IconClipboardList,
  IconLock,
  IconStethoscope,
  IconTrendingUp,
  IconUsers,
  TablerIcon,
} from '@tabler/icons-react';
import LogoImage from '/arkon-logo-small.png';

type FeatureCardProps = {
  title: string;
  desc: string;
  icon: TablerIcon;
};

const FeatureCard: FC<FeatureCardProps> = ({
  title = '',
  desc = '',
  icon: Icon,
}) => (
  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
    <Card shadow="sm" padding="xl" radius="md" className={classes.featureBox}>
      <Group mb="md">
        <Icon size={20} />
        <Title order={3} style={{ fontSize: '15px' }}>
          {title}
        </Title>
      </Group>
      <Text>{desc}</Text>
    </Card>
  </Grid.Col>
);

export function ContactUsButton(): JSX.Element {
  const { t } = useTranslation(['common']);
  const email = 'rnd@ainigma.tech';
  const subject = t('emailSubject');
  const body = t('emailBody');

  return (
    <Button
      onClick={() => {
        window.location.href = `mailto:${email}?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`;
      }}
      variant="transparent"
      color="white"
      style={{ '--button-hover': '#1a9988' }}
      className={classes.heroHeaderButton}
    >
      {t('contactUs')}
    </Button>
  );
}

export default function LandingPage(): JSX.Element {
  const { t } = useTranslation(['common', 'landing-page']);
  const navigate = useMedplumNavigate();

  const { selectedLanguage, changeLanguage } = useLanguageSelector();
  const isEnglish = selectedLanguage.code === 'en';

  return (
    <Box>
      <Box className={classes.heroSection}>
        <Image
          src={DoctorOfficeImage}
          alt="Doctor Office"
          className={classes.heroImage}
        />
        <Overlay color="#000" backgroundOpacity={0.7} zIndex={1} />
        <Group justify="space-between" p="md" className={classes.heroHeader}>
          <Group>
            <Image
              src={LogoImage}
              alt="Arkon Health Logo"
              h={50}
              w="auto"
            />
            <Title order={2} className={classes.heroHeaderName}>
              Arkon Health
            </Title>
          </Group>
          <Group>
            <Button
              variant="transparent"
              color="white"
              style={{ '--button-hover': '#1a9988' }}
              className={classes.heroHeaderButton}
              onClick={() => changeLanguage(isEnglish ? 'el' : 'en')}
            >
              {isEnglish ? 'En' : 'Ελ'}
            </Button>
            <Button
              variant="transparent"
              color="white"
              style={{ '--button-hover': '#1a9988' }}
              onClick={() => navigate('/signin')}
              className={classes.heroHeaderButton}
            >
              {t('logIn')}
            </Button>
            <ContactUsButton />
          </Group>
        </Group>
        <Container className={classes.heroContent}>
          <Title order={1} fw={700} className={classes.heroTitle}>
            {t('mainTitle', { ns: 'landing-page' })}
          </Title>
          <Text className={classes.heroDescription}>
            {t('description', { ns: 'landing-page' })}
          </Text>
        </Container>
      </Box>
      <Space h="xl" />
      <Container mt="xl" mb="xl" size="lg" my="lg">
        <Title order={2} mb="xl" className={classes.featuteTitle}>
          {t('keyFeatures', { ns: 'landing-page' })}
        </Title>
        <Grid gutter="xl" align="stretch">
          <FeatureCard
            title={t('feature1.title', { ns: 'landing-page' })}
            desc={t('feature1.desc', { ns: 'landing-page' })}
            icon={IconClipboardList}
          />
          <FeatureCard
            title={t('feature2.title', { ns: 'landing-page' })}
            desc={t('feature2.desc', { ns: 'landing-page' })}
            icon={IconCalendar}
          />
          <FeatureCard
            title={t('feature3.title', { ns: 'landing-page' })}
            desc={t('feature3.desc', { ns: 'landing-page' })}
            icon={IconTrendingUp}
          />
          <FeatureCard
            title={t('feature4.title', { ns: 'landing-page' })}
            desc={t('feature4.desc', { ns: 'landing-page' })}
            icon={IconUsers}
          />
          <FeatureCard
            title={t('feature5.title', { ns: 'landing-page' })}
            desc={t('feature5.desc', { ns: 'landing-page' })}
            icon={IconLock}
          />
          <FeatureCard
            title={t('feature6.title', { ns: 'landing-page' })}
            desc={t('feature6.desc', { ns: 'landing-page' })}
            icon={IconStethoscope}
          />
        </Grid>
      </Container>
      <Space h="xl" />
      <Text mb="xs" className={classes.footer}>
        {t('copyrightNotice')}
      </Text>
    </Box>
  );
}
